import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    FaWarehouse, FaPlus, FaEdit, FaTrash, FaTimes,
    FaExclamationTriangle, FaMapMarkerAlt, FaCheck,
    FaChevronLeft, FaChevronRight, FaRegCalendarAlt,
} from "react-icons/fa";
import { BsBoxSeam } from "react-icons/bs";
import { getUserRole, getFPOData } from "../../helpers/local_storage_helpers";
import {
    getWarehouses,
    addWarehouse,
    updateWarehouse,
    deleteWarehouse,
} from "../../services/fpoWarehouse";
import {
    getProcurements,
    getProcurementSummary,
    approveProcurement,
    rejectProcurement,
    bulkApproveProcurements,
    createProcurement,
    updateProcurement,
    getStockBatches,
    getStockMovements,
    getStockSummary,
    getFarmerPayments,
    createFarmerPayment,
    getLedgerFarmers,
    getLedgerFarmerEntries,
    postManualLedgerAdjustment,
} from "../../services/fpoProcurement";
import { getFPOCommodities } from "../../services/fpoCommodity";
import { getFPOMembers, getFPOMemberLocations } from "../../services/fpoMember";
import SearchableSelect from "../../components/inputFields/SearchableSelect";


// ─────────────────────────────────────────────
// Role helpers
// ─────────────────────────────────────────────
const useRole = () => {
    const role = getUserRole(); // reads user.role from localStorage
    return {
        role,
        isAdmin:      role === "fpo_admin" || role === "manager" || !role,
        isAccountant: role === "accountant",
        isDataEntry:  role === "data_entry" || role === "field_staff",
        canApprove:   role === "fpo_admin" || role === "manager" || !role,
        canPayment:   role === "fpo_admin" || role === "manager" || role === "accountant" || !role,
    };
};

// ─────────────────────────────────────────────
// Shared formatting helpers
// ─────────────────────────────────────────────
const fmt = (val, decimals = 2) => {
    if (val == null || val === "") return "—";
    const n = parseFloat(val);
    if (isNaN(n)) return val;
    return n % 1 === 0 ? n.toString() : n.toFixed(decimals).replace(/\.?0+$/, "");
};

const fmtMoney = (val) => {
    if (val == null || val === "") return "—";
    const n = parseFloat(val);
    if (isNaN(n)) return "—";
    return `₹${n.toLocaleString("en-IN")}`;
};

const fmtDate = (val) => {
    if (!val) return "—";
    try { return new Date(val).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return val; }
};

// Pulls a human-readable message out of a DRF error body — {detail}, {message},
// or a field-error object like {farmer: ["This field is required."]}.
const extractApiError = (e, fallback) => {
    const data = e?.response?.data;
    if (data?.detail) return data.detail;
    if (data?.message) return data.message;
    if (data && typeof data === "object") {
        const joined = Object.entries(data)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : v}`)
            .join(" ");
        if (joined) return joined;
    }
    return fallback;
};

// ─────────────────────────────────────────────
// Modal shell
// ─────────────────────────────────────────────
const Modal = ({ title, subtitle, onClose, children, wide }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
    >
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            onClick={(e) => e.stopPropagation()}
            className={`bg-[#f5f0e8] rounded-2xl shadow-2xl w-full ${wide ? "max-w-lg" : "max-w-md"} max-h-[90vh] overflow-y-auto`}
        >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
                <div>
                    <h3 className="font-bold text-gray-900 text-base">{title}</h3>
                    {subtitle && <p className="text-gray-500 text-xs mt-0.5">{subtitle}</p>}
                </div>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-lg transition-colors ml-4 flex-shrink-0"
                >
                    <FaTimes size={14} />
                </button>
            </div>
            <div className="px-6 pb-6">{children}</div>
        </motion.div>
    </motion.div>
);

// ─────────────────────────────────────────────
// Toggle switch
// ─────────────────────────────────────────────
const Toggle = ({ checked, onChange, id }) => (
    <button
        id={id}
        type="button"
        onClick={onChange}
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
            checked ? "bg-primary" : "bg-gray-300"
        }`}
    >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
            checked ? "translate-x-5" : "translate-x-0"
        }`} />
    </button>
);

// ─────────────────────────────────────────────
// Status pill
// ─────────────────────────────────────────────
const STATUS_STYLES = {
    pending:   "bg-amber-50 text-amber-700 border border-amber-200",
    approved:  "bg-green-50 text-green-700 border border-green-200",
    rejected:  "bg-red-50 text-red-600 border border-red-200",
    paid:      "bg-blue-50 text-blue-700 border border-blue-200",
    partial:   "bg-purple-50 text-purple-700 border border-purple-200",
    completed: "bg-blue-50 text-blue-700 border border-blue-200",
    failed:    "bg-red-50 text-red-600 border border-red-200",
};

const StatusPill = ({ status }) => {
    if (!status) return <span className="text-gray-400 text-sm">—</span>;
    const s = status.toLowerCase();
    return (
        <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${STATUS_STYLES[s] || "bg-gray-100 text-gray-500"}`}>
            {status}
        </span>
    );
};

// ─────────────────────────────────────────────
// Simple pagination
// ─────────────────────────────────────────────
const Pagination = ({ page, total, pageSize, onPage }) => {
    const pages = Math.ceil(total / pageSize) || 1;
    if (pages <= 1) return null;
    return (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
            <span className="text-xs text-gray-400">{total} total</span>
            <div className="flex items-center gap-1">
                <button
                    disabled={page <= 1}
                    onClick={() => onPage(page - 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <FaChevronLeft size={10} />
                </button>
                <span className="text-xs text-gray-600 px-2">{page} / {pages}</span>
                <button
                    disabled={page >= pages}
                    onClick={() => onPage(page + 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <FaChevronRight size={10} />
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// Loading / empty states
// ─────────────────────────────────────────────
const Spinner = ({ label = "Loading…" }) => (
    <div className="flex flex-col items-center justify-center h-48 gap-3">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">{label}</p>
    </div>
);

const Empty = ({ icon: Icon = BsBoxSeam, label, action }) => (
    <div className="flex flex-col items-center justify-center h-48 gap-3 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
            <Icon className="text-gray-300 text-2xl" />
        </div>
        <p className="font-semibold text-gray-400 text-sm">{label}</p>
        {action}
    </div>
);

const ErrState = ({ msg, onRetry }) => (
    <div className="flex flex-col items-center justify-center h-48 gap-3">
        <p className="text-red-500 font-medium text-sm">{msg}</p>
        {onRetry && <button onClick={onRetry} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold">Retry</button>}
    </div>
);

// ─────────────────────────────────────────────
// APPROVE MODAL
// ─────────────────────────────────────────────
const ApproveModal = ({ procurement, warehouses, onClose, onApproved }) => {
    const [warehouseId, setWarehouseId] = useState(procurement.warehouse || "");
    const [saving, setSaving] = useState(false);

    const amount = procurement.amount ? fmtMoney(procurement.amount) : "—";
    const farmerName = procurement.farmer_name || procurement.farmer?.name || "Farmer";
    const prcNo = procurement.number || procurement.id?.slice(0, 8);

    const handleApprove = async () => {
        setSaving(true);
        try {
            await approveProcurement(procurement.id, warehouseId ? { warehouse: warehouseId } : {});
            toast.success(`${prcNo} approved ✅`);
            onApproved();
            onClose();
        } catch (e) {
            toast.error(extractApiError(e, "Approval failed."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={`Approve ${prcNo}`}
            subtitle={`One atomic step: create batch ${prcNo}, add ${fmt(procurement.quantity)} qtl of stock, credit ${amount} to ${farmerName}. Re-approving is a no-op.`}
            onClose={onClose}
        >
            <div className="flex flex-col gap-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Warehouse (optional override)
                    </label>
                    <SearchableSelect
                        value={warehouseId}
                        onChange={setWarehouseId}
                        placeholder="Use entry's warehouse / FPO default"
                        allowClear
                        options={warehouses.map((w) => ({ value: w.id, label: `${w.name}${w.is_default ? " (default)" : ""}` }))}
                        className="px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                    />
                </div>
                <div className="flex gap-3 pt-2 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApprove}
                        disabled={saving}
                        className="flex-1 py-2.5 bg-primary hover:bg-green-700 text-white font-semibold rounded-xl text-sm flex items-center justify-center gap-2 shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                    >
                        <FaCheck size={11} />
                        {saving ? "Approving…" : "Approve"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────
// REJECT MODAL
// ─────────────────────────────────────────────
const RejectModal = ({ procurement, onClose, onRejected }) => {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const prcNo = procurement.number || procurement.id?.slice(0, 8);

    const handleReject = async () => {
        if (!reason.trim()) { toast.error("Rejection reason is required."); return; }
        setSaving(true);
        try {
            await rejectProcurement(procurement.id, { rejection_reason: reason.trim() });
            toast.success(`${prcNo} rejected`);
            onRejected();
            onClose();
        } catch (e) {
            toast.error(extractApiError(e, "Rejection failed."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title={`Reject ${prcNo}`} subtitle="A reason is required — it's stored on the record for the farmer/audit trail." onClose={onClose}>
            <div className="flex flex-col gap-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Moisture too high, quality on hold…"
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white resize-none"
                    />
                </div>
                <div className="flex gap-3 pt-2 border-t border-gray-200">
                    <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors">
                        Cancel
                    </button>
                    <button type="button" onClick={handleReject} disabled={saving}
                        className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-60">
                        {saving ? "Rejecting…" : "Reject entry"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────
// PAY FARMER MODAL
// ─────────────────────────────────────────────
const PAYMENT_MODES = [
    { value: "bank_transfer", label: "Bank transfer" },
    { value: "cash",          label: "Cash" },
    { value: "cheque",        label: "Cheque" },
    { value: "upi",           label: "UPI" },
];

// `procurement` is optional — when omitted this opens as a standalone payment
// against a directly-picked farmer (requires `members` for the farmer dropdown).
const PayFarmerModal = ({ procurement, members = [], onClose, onPaid }) => {
    const prcNo = procurement ? (procurement.number || procurement.id?.slice(0, 8)) : null;
    const outstanding = procurement
        ? parseFloat(procurement.amount || 0) - parseFloat(procurement.amount_paid || 0)
        : 0;
    const farmerName = procurement ? (procurement.farmer_name || procurement.farmer?.name || "Farmer") : null;

    const [farmerId, setFarmerId]   = useState(procurement?.farmer?.id || procurement?.farmer || "");
    const [amount, setAmount]       = useState(outstanding > 0 ? String(outstanding) : "");
    const [mode, setMode]           = useState("bank_transfer");
    const [reference, setReference] = useState("");
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
    const [saving, setSaving]       = useState(false);

    const handlePay = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
        if (!procurement && !farmerId) { toast.error("Select a farmer."); return; }
        setSaving(true);
        try {
            const payload = {
                amount: amt,
                payment_mode: mode,
                reference_no: reference.trim(),
                payment_date: paymentDate,
            };
            if (procurement) payload.procurement = procurement.id;
            else payload.farmer = farmerId;
            await createFarmerPayment(payload);
            toast.success(`Payment of ₹${amt.toLocaleString("en-IN")} recorded ✅`);
            onPaid();
            onClose();
        } catch (e) {
            toast.error(extractApiError(e, "Payment failed."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={procurement ? `Pay farmer · ${prcNo}` : "Record a farmer payment"}
            subtitle={procurement
                ? `Outstanding ${fmtMoney(outstanding)} to ${farmerName}. The matching debit is posted here in the view, not through a signal.`
                : "Pick a farmer and record a payout not tied to a specific procurement line."}
            onClose={onClose}
        >
            <div className="flex flex-col gap-4">
                {!procurement && (
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Farmer <span className="text-red-500">*</span>
                        </label>
                        <SearchableSelect
                            value={farmerId}
                            onChange={setFarmerId}
                            placeholder="Select farmer…"
                            searchPlaceholder="Search farmers…"
                            options={members.map((m) => ({
                                value: m.user_id || m.id,
                                label: m.name || m.full_name || m.username || `Member #${m.id}`,
                            }))}
                            className="px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                        />
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Amount</label>
                        <input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Mode</label>
                        <SearchableSelect
                            value={mode}
                            onChange={setMode}
                            options={PAYMENT_MODES}
                            className="px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Reference (UTR / cheque no)
                        </label>
                        <input
                            type="text"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="Optional"
                            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Payment date</label>
                        <input
                            type="date"
                            value={paymentDate}
                            onChange={(e) => setPaymentDate(e.target.value)}
                            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white"
                        />
                    </div>
                </div>
                <div className="flex gap-3 pt-2 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handlePay}
                        disabled={saving}
                        className="flex-1 py-2.5 bg-primary hover:bg-green-700 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                    >
                        {saving ? "Recording…" : "Record payment"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────
// PROCUREMENT FORM MODAL (Add / Edit)
// ─────────────────────────────────────────────
const GRADE_OPTIONS  = ["FAQ", "A", "B", "C", "Premium"];
const QC_OPTIONS = [
    { value: "accepted",  label: "Accepted" },
    { value: "on_hold",   label: "On Hold" },
    { value: "rejected",  label: "Rejected" },
];

const EMPTY_PRC = {
    farmer:           "",
    fpo_commodity:    "",
    grade:            "",
    quantity:         "",
    rate:             "",
    moisture_percent: "",
    quality_check:    "",
    warehouse:        "",
    procurement_date: new Date().toISOString().slice(0, 10),
    notes:            "",
};

const ProcurementFormModal = ({ editing, warehouses, onClose, onSaved }) => {
    const [form, setForm]     = useState(editing ? {
        farmer:           editing.farmer || "",
        fpo_commodity:    editing.fpo_commodity || "",
        grade:            editing.grade || "",
        quantity:         editing.quantity != null ? String(editing.quantity) : "",
        rate:             editing.rate != null ? String(editing.rate) : "",
        moisture_percent: editing.moisture_percent != null ? String(editing.moisture_percent) : "",
        quality_check:    editing.quality_check || "",
        warehouse:        editing.warehouse || "",
        procurement_date: editing.procurement_date || new Date().toISOString().slice(0, 10),
        notes:            editing.notes || "",
    } : { ...EMPTY_PRC });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // dropdown data
    const [members, setMembers]     = useState([]);
    const [commodities, setCommodities] = useState([]);
    const [dropLoading, setDropLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setDropLoading(true);
            try {
                const [mRes, cRes] = await Promise.all([
                    getFPOMembers({ page_size: 500 }),
                    getFPOCommodities(),
                ]);
                // members: DRF paginated
                const mData = mRes?.data;
                setMembers(mData?.results || mData?.data || []);
                // commodities: may be paginated or wrapped
                const cData = cRes?.data;
                setCommodities(cData?.results || cData?.data || (Array.isArray(cData) ? cData : []));
            } catch {
                toast.error("Failed to load dropdown data.");
            } finally {
                setDropLoading(false);
            }
        };
        load();
    }, []);

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const errs = {};
        if (!form.farmer)        errs.farmer        = "Farmer is required";
        if (!form.fpo_commodity) errs.fpo_commodity = "Commodity is required";
        if (!form.quantity || isNaN(parseFloat(form.quantity))) errs.quantity = "Valid quantity required";
        if (!form.rate    || isNaN(parseFloat(form.rate)))    errs.rate    = "Valid rate required";
        if (!form.procurement_date) errs.procurement_date = "Date is required";
        return errs;
    };

    const handleSubmit = async () => {
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setSaving(true);
        try {
            const payload = {
                farmer:           form.farmer,
                fpo_commodity:    form.fpo_commodity,
                grade:            form.grade || null,
                quantity:         parseFloat(form.quantity),
                rate:             parseFloat(form.rate),
                moisture_percent: form.moisture_percent !== "" ? parseFloat(form.moisture_percent) : null,
                warehouse:        form.warehouse || null,
                procurement_date: form.procurement_date,
                notes:            form.notes.trim(),
            };
            // quality_check is a choice field — the server 400s on "" ("not a valid
            // choice") and on null, so only send it when the user actually picked one.
            if (form.quality_check) payload.quality_check = form.quality_check;
            if (editing) await updateProcurement(editing.id, payload);
            else await createProcurement(payload);
            toast.success(editing ? "Procurement entry updated! ✅" : "Procurement entry saved! ✅");
            onSaved();
            onClose();
        } catch (e) {
            const errsData = e?.response?.data;
            if (errsData && typeof errsData === "object" && !errsData.detail) {
                // field-level errors
                const fieldErrs = {};
                Object.entries(errsData).forEach(([k, v]) => {
                    fieldErrs[k] = Array.isArray(v) ? v.join(" ") : String(v);
                });
                setErrors(fieldErrs);
                toast.error("Please fix the errors below.");
            } else {
                const msg = errsData?.detail || errsData?.message || "Something went wrong.";
                toast.error(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const iCls  = (k) => `w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white ${errors[k] ? "border-red-400" : "border-gray-300"}`;
    const errEl = (k) => errors[k] ? <p className="text-red-500 text-[11px] mt-1">{errors[k]}</p> : null;

    return (
        <Modal
            title={editing ? "Edit procurement" : "New procurement entry"}
            subtitle="Record a farmer's produce purchase."
            onClose={onClose}
            wide
        >
            {dropLoading ? (
                <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            ) : (
                <div className="flex flex-col gap-3.5">
                    {/* Farmer */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Farmer <span className="text-red-500">*</span>
                        </label>
                        <SearchableSelect
                            value={form.farmer}
                            onChange={(v) => set("farmer", v)}
                            placeholder="Select farmer…"
                            searchPlaceholder="Search farmers…"
                            options={members.map((m) => ({
                                value: m.id,
                                label: `${m.name || m.full_name || m.username || `Member #${m.id}`}${m.village ? ` · ${m.village}` : ""}`,
                            }))}
                            className={iCls("farmer")}
                            error={!!errors.farmer}
                        />
                        {errEl("farmer")}
                    </div>

                    {/* Commodity */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Commodity <span className="text-red-500">*</span>
                        </label>
                        <SearchableSelect
                            value={form.fpo_commodity}
                            onChange={(v) => set("fpo_commodity", v)}
                            placeholder="Select commodity…"
                            searchPlaceholder="Search commodities…"
                            options={commodities.map((c) => ({
                                value: c.id,
                                label: c.commodity?.name || c.name || `Commodity #${c.id}`,
                            }))}
                            className={iCls("fpo_commodity")}
                            error={!!errors.fpo_commodity}
                        />
                        {errEl("fpo_commodity")}
                    </div>

                    {/* Grade */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Grade</label>
                        <SearchableSelect
                            value={form.grade}
                            onChange={(v) => set("grade", v)}
                            placeholder="Select grade…"
                            options={GRADE_OPTIONS.map((g) => ({ value: g, label: g }))}
                            className={iCls("grade")}
                        />
                    </div>

                    {/* Qty + Rate */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                Quantity (qtl) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number" min="0" step="0.01"
                                value={form.quantity} onChange={(e) => set("quantity", e.target.value)}
                                placeholder="e.g. 12"
                                className={iCls("quantity")}
                            />
                            {errEl("quantity")}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                Rate (₹/qtl) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number" min="0" step="0.01"
                                value={form.rate} onChange={(e) => set("rate", e.target.value)}
                                placeholder="e.g. 4820"
                                className={iCls("rate")}
                            />
                            {errEl("rate")}
                        </div>
                    </div>

                    {/* Moisture + QC */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Moisture (%)</label>
                            <input
                                type="number" min="0" max="100" step="0.1"
                                value={form.moisture_percent} onChange={(e) => set("moisture_percent", e.target.value)}
                                placeholder="e.g. 10.4"
                                className={iCls("moisture_percent")}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Quality check</label>
                            <SearchableSelect
                                value={form.quality_check}
                                onChange={(v) => set("quality_check", v)}
                                placeholder="Not checked"
                                allowClear
                                options={QC_OPTIONS}
                                className={iCls("quality_check")}
                            />
                        </div>
                    </div>

                    {/* Warehouse + Date */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Warehouse</label>
                            <SearchableSelect
                                value={form.warehouse}
                                onChange={(v) => set("warehouse", v)}
                                placeholder="No warehouse yet"
                                allowClear
                                options={warehouses.map((w) => ({ value: w.id, label: `${w.name}${w.is_default ? " (default)" : ""}` }))}
                                className={iCls("warehouse")}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={form.procurement_date}
                                onChange={(e) => set("procurement_date", e.target.value)}
                                className={iCls("procurement_date")}
                            />
                            {errEl("procurement_date")}
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notes</label>
                        <textarea
                            rows={2}
                            value={form.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            placeholder="Optional remarks…"
                            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white resize-none"
                        />
                    </div>

                    {/* Amount preview */}
                    {form.quantity && form.rate && !isNaN(parseFloat(form.quantity)) && !isNaN(parseFloat(form.rate)) && (
                        <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-2.5 flex items-center justify-between">
                            <span className="text-xs text-green-700 font-medium">Calculated amount</span>
                            <span className="font-bold text-green-800 text-sm">
                                {fmtMoney(parseFloat(form.quantity) * parseFloat(form.rate))}
                            </span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-2 border-t border-gray-200">
                        <button type="button" onClick={onClose}
                            className="flex-1 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors">
                            Cancel
                        </button>
                        <button type="button" onClick={handleSubmit} disabled={saving}
                            className="flex-1 py-2.5 bg-primary hover:bg-green-700 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary/20 transition-colors disabled:opacity-60">
                            {saving ? "Saving…" : editing ? "Update" : "Add entry"}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

// ─────────────────────────────────────────────
// PROCUREMENT REGISTER TAB
// ─────────────────────────────────────────────
const STATUS_FILTERS = ["all", "pending", "approved", "rejected"];
const PAYMENT_STATUS_FILTERS_REGISTER = ["all", "pending", "partial", "paid"];
const PAGE_SIZE = 20;

const toISODate = (d) => d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : "";

const ProcurementRegisterTab = ({ warehouses, canApprove, canPayment, isAccountant, isDataEntry, onDataChange }) => {
    const [rows, setRows]           = useState([]);
    const [total, setTotal]         = useState(0);
    const [page, setPage]           = useState(1);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);
    const [search, setSearch]       = useState("");
    const [statusFilter, setStatus] = useState("all");
    const [paymentStatusFilter, setPaymentStatus] = useState("all");
    const [village, setVillage]     = useState("");
    const [tehsil, setTehsil]       = useState("");
    const [villageOptions, setVillageOptions] = useState([]);
    const [tehsilOptions, setTehsilOptions]   = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [dateFrom, dateTo]        = dateRange;
    const [selected, setSelected]   = useState([]);
    const [bulkSaving, setBulkSaving] = useState(false);

    useEffect(() => {
        getFPOMemberLocations()
            .then((res) => {
                const data = res?.data?.data;
                setVillageOptions(data?.villages || []);
                setTehsilOptions(data?.tehsils || []);
            })
            .catch(() => {});
    }, []);

    // modals
    const [showAdd, setShowAdd]       = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [approvingRow, setApprovingRow] = useState(null);
    const [rejectingRow, setRejectingRow] = useState(null);
    const [payingRow, setPayingRow]       = useState(null);

    const canEdit = !isAccountant; // data_entry/field_staff can edit their own pending entries; accountant is read-only

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (search.trim()) params.search = search.trim();
            if (statusFilter !== "all") params.status = statusFilter;
            if (paymentStatusFilter !== "all") params.payment_status = paymentStatusFilter;
            if (village.trim()) params.village = village.trim();
            if (tehsil.trim()) params.tehsil = tehsil.trim();
            if (dateFrom) params.date_from = toISODate(dateFrom);
            if (dateTo) params.date_to = toISODate(dateTo);
            const res = await getProcurements(params);
            // DRF paginated: { count, next, previous, results }
            const data = res?.data;
            setRows(data?.results || []);
            setTotal(data?.count || 0);
            setSelected([]);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.response?.data?.message || "Unable to load procurements.");
        } finally {
            setLoading(false);
        }
    }, [page, search, statusFilter, paymentStatusFilter, village, tehsil, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const handleApproved = () => { load(); onDataChange?.(); };
    const handleRejected = () => { load(); onDataChange?.(); };
    const handlePaid     = () => { load(); onDataChange?.(); };
    const handleSaved    = () => { load(); onDataChange?.(); };

    const pendingRows = rows.filter((r) => r.status?.toLowerCase() === "pending");
    const allPendingSelected = pendingRows.length > 0 && pendingRows.every((r) => selected.includes(r.id));

    const toggleSelectAll = () => {
        setSelected(allPendingSelected ? [] : pendingRows.map((r) => r.id));
    };
    const toggleSelectRow = (id) => {
        setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
    };

    const handleBulkApprove = async () => {
        if (selected.length === 0) return;
        setBulkSaving(true);
        try {
            const res = await bulkApproveProcurements({ ids: selected });
            const results = res?.data?.results || [];
            const okCount = results.filter((r) => r.success).length;
            const failCount = results.length - okCount;
            if (failCount === 0) {
                toast.success(`${okCount} procurement(s) approved ✅`);
            } else {
                toast.warn(`${okCount} approved, ${failCount} failed — check rows and retry individually.`);
            }
            load();
            onDataChange?.();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.response?.data?.message || "Bulk approve failed.";
            toast.error(msg);
        } finally {
            setBulkSaving(false);
        }
    };

    return (
        <div>
            {/* Header row: title + Add button */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="font-bold text-gray-900 text-base">Procurement register</h2>
                    <p className="text-gray-400 text-xs mt-0.5">All inbound purchases — pending, approved, rejected</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {canApprove && selected.length > 0 && (
                        <button
                            type="button"
                            onClick={handleBulkApprove}
                            disabled={bulkSaving}
                            className="flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-lg shadow-primary/20 disabled:opacity-60"
                        >
                            <FaCheck size={11} /> {bulkSaving ? "Approving…" : `Approve Selected (${selected.length})`}
                        </button>
                    )}
                    {!isDataEntry && (
                        <button
                            id="add-procurement-btn"
                            type="button"
                            onClick={() => { setEditingRow(null); setShowAdd(true); }}
                            className="flex items-center gap-2 bg-primary hover:bg-green-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-lg shadow-primary/20"
                        >
                            <FaPlus size={11} /> New entry
                        </button>
                    )}
                </div>
            </div>

            {/* Search + status filter */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs">🔍</span>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        placeholder="Search PRC no, farmer, commodity"
                        className="w-full pl-8 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white"
                    />
                </div>
                <div className="flex gap-1 flex-shrink-0">
                    {STATUS_FILTERS.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => { setStatus(s); setPage(1); }}
                            className={`px-3 py-2 text-xs font-semibold rounded-lg capitalize transition-colors ${
                                statusFilter === s
                                    ? "bg-primary text-white shadow"
                                    : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                            }`}
                        >
                            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Payment status filter */}
            <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex-shrink-0">Payment</span>
                <div className="flex gap-1">
                    {PAYMENT_STATUS_FILTERS_REGISTER.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => { setPaymentStatus(s); setPage(1); }}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg capitalize transition-colors ${
                                paymentStatusFilter === s
                                    ? "bg-primary text-white shadow"
                                    : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                            }`}
                        >
                            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Village / tehsil / date-range filters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                <SearchableSelect
                    value={village}
                    onChange={(v) => { setVillage(v); setPage(1); }}
                    placeholder="All villages"
                    allowClear
                    options={villageOptions.map((v) => ({ value: v, label: v }))}
                    className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white text-gray-600"
                />
                <SearchableSelect
                    value={tehsil}
                    onChange={(v) => { setTehsil(v); setPage(1); }}
                    placeholder="All tehsils"
                    allowClear
                    options={tehsilOptions.map((t) => ({ value: t, label: t }))}
                    className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white text-gray-600"
                />
                <div className="relative">
                    <FaRegCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-primary text-xs pointer-events-none z-10" />
                    <DatePicker
                        selectsRange
                        startDate={dateFrom}
                        endDate={dateTo}
                        onChange={(update) => { setDateRange(update); setPage(1); }}
                        isClearable
                        placeholderText="Select date range"
                        dateFormat="dd MMM yyyy"
                        wrapperClassName="w-full"
                        className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white text-gray-600"
                    />
                </div>
            </div>

            {/* Table */}
            {loading ? <Spinner label="Loading procurements…" /> : error ? (
                <ErrState msg={error} onRetry={load} />
            ) : rows.length === 0 ? (
                <Empty label="No procurements found" />
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    {canApprove && (
                                        <th className="py-2 px-3 pl-0 w-8">
                                            <input
                                                type="checkbox"
                                                checked={allPendingSelected}
                                                onChange={toggleSelectAll}
                                                disabled={pendingRows.length === 0}
                                                className="w-3.5 h-3.5"
                                            />
                                        </th>
                                    )}
                                    {["PRC", "FARMER", "COMMODITY", "QTY", "RATE", "AMOUNT", "STATUS", "ACTION"].map((h) => (
                                        <th key={h} className="py-2 px-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap first:pl-0 last:pr-0">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {rows.map((row) => {
                                    const prcNo   = row.number || row.id?.slice(0, 8);
                                    const farmer  = row.farmer_name || row.farmer?.name || "—";
                                    const village = row.farmer_village || row.farmer?.village || "";
                                    const comm    = row.commodity_name || row.fpo_commodity?.name || "—";
                                    const grade   = row.grade ? `Grade ${row.grade}` : "";
                                    const moisture = row.moisture_percent ? `${row.moisture_percent}% moisture` : "";
                                    const gradeStr = [grade, moisture].filter(Boolean).join(" · ");
                                    const isPending  = row.status?.toLowerCase() === "pending";

                                    return (
                                        <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                                            {canApprove && (
                                                <td className="py-3 px-3 pl-0">
                                                    {isPending && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selected.includes(row.id)}
                                                            onChange={() => toggleSelectRow(row.id)}
                                                            className="w-3.5 h-3.5"
                                                        />
                                                    )}
                                                </td>
                                            )}
                                            <td className="py-3 px-3 pl-0 whitespace-nowrap">
                                                <p className="font-bold text-gray-800 text-xs">{prcNo}</p>
                                                <p className="text-[10px] text-gray-400">{fmtDate(row.procurement_date)}</p>
                                            </td>
                                            <td className="py-3 px-3 whitespace-nowrap">
                                                <p className="font-medium text-gray-800 text-xs">{farmer}</p>
                                                {village && <p className="text-[10px] text-gray-400">{village}</p>}
                                            </td>
                                            <td className="py-3 px-3">
                                                <p className="font-medium text-gray-800 text-xs">{comm}</p>
                                                {gradeStr && <p className="text-[10px] text-gray-400">{gradeStr}</p>}
                                            </td>
                                            <td className="py-3 px-3 whitespace-nowrap text-xs text-gray-700">
                                                {fmt(row.quantity)} qtl
                                            </td>
                                            <td className="py-3 px-3 whitespace-nowrap text-xs text-gray-700">
                                                {fmtMoney(row.rate)}
                                            </td>
                                            <td className="py-3 px-3 whitespace-nowrap">
                                                <span className="font-bold text-gray-800 text-xs">{fmtMoney(row.amount)}</span>
                                            </td>
                                            <td className="py-3 px-3 whitespace-nowrap">
                                                <StatusPill status={row.status} />
                                            </td>
                                            <td className="py-3 px-3 pr-0 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    {isPending && canApprove && (
                                                        <>
                                                            <button
                                                                onClick={() => setApprovingRow(row)}
                                                                className="px-3 py-1.5 bg-primary hover:bg-green-700 text-white text-xs font-bold rounded-lg transition-colors"
                                                            >
                                                                Approve
                                                            </button>
                                                            <button
                                                                onClick={() => setRejectingRow(row)}
                                                                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Reject"
                                                            >
                                                                <FaTimes size={10} />
                                                            </button>
                                                        </>
                                                    )}
                                                    {isPending && canEdit && (
                                                        <button
                                                            onClick={() => { setEditingRow(row); setShowAdd(true); }}
                                                            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
                                                            title="Edit"
                                                        >
                                                            <FaEdit size={10} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
                </>
            )}

            {/* Modals */}
            <AnimatePresence>
                {showAdd && (
                    <ProcurementFormModal
                        editing={editingRow}
                        warehouses={warehouses}
                        onClose={() => { setShowAdd(false); setEditingRow(null); }}
                        onSaved={handleSaved}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {approvingRow && (
                    <ApproveModal
                        procurement={approvingRow}
                        warehouses={warehouses}
                        onClose={() => setApprovingRow(null)}
                        onApproved={handleApproved}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {rejectingRow && (
                    <RejectModal
                        procurement={rejectingRow}
                        onClose={() => setRejectingRow(null)}
                        onRejected={handleRejected}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {payingRow && (
                    <PayFarmerModal
                        procurement={payingRow}
                        onClose={() => setPayingRow(null)}
                        onPaid={handlePaid}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

// ─────────────────────────────────────────────
// STOCK & MOVEMENTS TAB
// ─────────────────────────────────────────────
const StockMovementsTab = () => {
    const [batches, setBatches]     = useState([]);
    const [movements, setMovements] = useState([]);
    const [commSummary, setCommSummary] = useState([]);
    const [batchTotal, setBatchTotal]   = useState(0);
    const [movTotal, setMovTotal]       = useState(0);
    const [batchPage, setBatchPage]     = useState(1);
    const [movPage, setMovPage]         = useState(1);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [bRes, mRes, sRes] = await Promise.all([
                getStockBatches({ page: batchPage, page_size: 20 }),
                getStockMovements({ page: movPage, page_size: 20 }),
                getStockSummary(),
            ]);
            const bd = bRes?.data;
            const md = mRes?.data;
            setBatches(bd?.results || []);
            setBatchTotal(bd?.count || 0);
            setMovements(md?.results || []);
            setMovTotal(md?.count || 0);
            setCommSummary(sRes?.data?.results || []);
        } catch (e) {
            setError(e?.response?.data?.message || "Failed to load stock data.");
        } finally {
            setLoading(false);
        }
    }, [batchPage, movPage]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Spinner label="Loading stock data…" />;
    if (error)   return <ErrState msg={error} onRetry={load} />;

    return (
        <div>
            {commSummary.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                    {commSummary.map((c) => (
                        <div key={c.commodity_id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
                            <div className="flex items-center justify-between gap-1">
                                <p className="font-semibold text-gray-800 text-xs truncate">{c.commodity_name}</p>
                                {c.low_stock && (
                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 flex-shrink-0">
                                        LOW
                                    </span>
                                )}
                            </div>
                            <p className="font-bold text-gray-900 text-sm mt-1">{fmt(c.total_quantity)} {c.unit}</p>
                            <p className="text-[11px] text-gray-400">avg {fmtMoney(c.weighted_avg_cost)}/{c.unit} · {fmtMoney(c.total_value)}</p>
                        </div>
                    ))}
                </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Stock Batches */}
            <div className="bg-gray-50/60 rounded-xl p-4 border border-gray-100">
                <div className="mb-3">
                    <h3 className="font-bold text-gray-800 text-sm">Stock batches</h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">Auto-created on approval, batch no = procurement no</p>
                </div>
                {batches.length === 0 ? (
                    <Empty label="No stock batches yet" />
                ) : (
                    <div className="flex flex-col gap-2">
                        {batches.map((b) => {
                            const comm    = b.commodity_name || b.fpo_commodity?.name || "—";
                            const prcNo   = b.batch_no || b.id?.slice(0, 8);
                            const wh      = b.warehouse_name || b.warehouse?.name || "—";
                            const grade   = b.grade ? `Grade ${b.grade}` : "";
                            const supplier = b.supplier ? `from ${b.supplier}` : "";
                            const sub     = [wh, grade, supplier].filter(Boolean).join(" · ");

                            return (
                                <div key={b.id} className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm">
                                    <div className="flex items-start justify-between">
                                        <div className="min-w-0">
                                            <p className="font-semibold text-gray-800 text-xs">{comm} · {prcNo}</p>
                                            <p className="text-[11px] text-gray-400 mt-0.5 truncate">{sub}</p>
                                        </div>
                                        <div className="text-right flex-shrink-0 ml-2">
                                            <p className="font-bold text-gray-800 text-sm">{fmt(b.quantity)} qtl</p>
                                            <p className="text-[11px] text-gray-400">{fmtMoney(b.value)}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <Pagination page={batchPage} total={batchTotal} pageSize={20} onPage={setBatchPage} />
                    </div>
                )}
            </div>

            {/* Stock Movements */}
            <div className="bg-gray-50/60 rounded-xl p-4 border border-gray-100">
                <div className="mb-3">
                    <h3 className="font-bold text-gray-800 text-sm">Stock movements</h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">Append-only. Signed quantities must sum to batch quantity.</p>
                </div>
                {movements.length === 0 ? (
                    <Empty label="No movements recorded yet" />
                ) : (
                    <div className="flex flex-col gap-2">
                        {movements.map((m) => {
                            const qty   = parseFloat(m.quantity || 0);
                            const isPos = qty >= 0;
                            const prcNo = m.procurement_number || m.batch_no || "";
                            const wh    = m.warehouse_name || m.warehouse?.name || "";
                            const time  = m.created_at ? new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
                            const sub   = [prcNo, wh, time].filter(Boolean).join(" · ");

                            return (
                                <div key={m.id} className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="font-semibold text-gray-700 text-xs">{m.movement_type || m.type || "—"}</p>
                                        <p className="text-[11px] text-gray-400 mt-0.5 truncate">{sub}</p>
                                    </div>
                                    <span className={`font-bold text-sm flex-shrink-0 ${isPos ? "text-green-600" : "text-red-500"}`}>
                                        {isPos ? "+" : ""}{fmt(qty)} qtl
                                    </span>
                                </div>
                            );
                        })}
                        <Pagination page={movPage} total={movTotal} pageSize={20} onPage={setMovPage} />
                    </div>
                )}
            </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// FARMER LEDGER TAB
// ─────────────────────────────────────────────
const DEBIT_CREDIT_STYLES = {
    debit:  "bg-amber-50 text-amber-700 border border-amber-200",
    credit: "bg-green-50 text-green-700 border border-green-200",
};

// GET /fpo/ledger/farmers/{user_id}/manual-adjustment/ — fpo_admin only
const ManualAdjustmentModal = ({ farmer, onClose, onSaved }) => {
    const [entryType, setEntryType]   = useState("credit");
    const [amount, setAmount]         = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving]         = useState(false);

    const handleSubmit = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
        if (!description.trim()) { toast.error("Description is required."); return; }
        setSaving(true);
        try {
            await postManualLedgerAdjustment(farmer.farmer_id, {
                entry_type: entryType,
                amount: amt,
                description: description.trim(),
            });
            toast.success("Manual adjustment posted ✅");
            onSaved();
            onClose();
        } catch (e) {
            toast.error(extractApiError(e, "Adjustment failed."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title={`Manual adjustment · ${farmer.farmer_name}`} subtitle="fpo_admin only — posts a standalone credit/debit entry." onClose={onClose}>
            <div className="flex flex-col gap-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Entry type</label>
                    <div className="flex gap-2">
                        {["credit", "debit"].map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setEntryType(t)}
                                className={`flex-1 py-2 text-xs font-bold rounded-lg capitalize transition-colors ${
                                    entryType === t ? "bg-primary text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Amount</label>
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Description <span className="text-red-500">*</span>
                    </label>
                    <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                        placeholder="Reason for this manual entry…"
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white resize-none" />
                </div>
                <div className="flex gap-3 pt-2 border-t border-gray-200">
                    <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors">
                        Cancel
                    </button>
                    <button type="button" onClick={handleSubmit} disabled={saving}
                        className="flex-1 py-2.5 bg-primary hover:bg-green-700 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary/20 transition-colors disabled:opacity-60">
                        {saving ? "Posting…" : "Post adjustment"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// `isAdminRole` (fpo_admin only, not manager) gates the manual-adjustment action.
const FarmerLedgerTab = ({ isAdminRole }) => {
    const [farmers, setFarmers]       = useState([]);
    const [statement, setStatement]   = useState(null);
    const [selectedFarmer, setSelectedFarmer] = useState(null);
    const [farmersLoading, setFarmersLoading] = useState(true);
    const [entriesLoading, setEntriesLoading] = useState(false);
    const [error, setError]           = useState(null);
    const [showAdjust, setShowAdjust] = useState(false);

    // GET /fpo/ledger/farmers/ — wrapped {status, message, results: [{farmer_id, farmer_name, farmer_mobile, credit_total, debit_total, balance}]}
    const loadFarmers = useCallback(async () => {
        setFarmersLoading(true);
        try {
            const res  = await getLedgerFarmers();
            const list = res?.data?.results || [];
            setFarmers(list);
            setSelectedFarmer((prev) => prev || list[0] || null);
        } catch (e) {
            setError(e?.response?.data?.message || "Failed to load farmers.");
        } finally {
            setFarmersLoading(false);
        }
    }, []);

    useEffect(() => { loadFarmers(); }, [loadFarmers]);

    // GET /fpo/ledger/farmers/{user_id}/ — wrapped {status, message, data: {..., entries: [...]}}
    const loadStatement = useCallback(async () => {
        if (!selectedFarmer) return;
        setEntriesLoading(true);
        try {
            const res = await getLedgerFarmerEntries(selectedFarmer.farmer_id);
            setStatement(res?.data?.data || null);
        } catch {
            setStatement(null);
        } finally {
            setEntriesLoading(false);
        }
    }, [selectedFarmer]);

    useEffect(() => { loadStatement(); }, [loadStatement]);

    const entries = statement?.entries || [];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Farmer balances */}
            <div className="bg-gray-50/60 rounded-xl p-4 border border-gray-100">
                <div className="mb-3">
                    <h3 className="font-bold text-gray-800 text-sm">Farmer balances</h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">credit − debit = what we owe</p>
                </div>
                {farmersLoading ? (
                    <Spinner label="Loading farmers…" />
                ) : error ? (
                    <ErrState msg={error} />
                ) : farmers.length === 0 ? (
                    <Empty label="No farmer ledger entries yet" />
                ) : (
                    <div className="flex flex-col gap-0.5">
                        {farmers.map((f) => {
                            const balance = parseFloat(f.balance || 0);
                            const isSelected = selectedFarmer?.farmer_id === f.farmer_id;
                            return (
                                <button
                                    key={f.farmer_id}
                                    type="button"
                                    onClick={() => setSelectedFarmer(f)}
                                    className={`w-full flex items-center justify-between px-3 py-3 rounded-xl text-left transition-colors ${
                                        isSelected
                                            ? "bg-white border border-primary/20 shadow-sm"
                                            : "hover:bg-white/70"
                                    }`}
                                >
                                    <div>
                                        <p className="font-semibold text-gray-800 text-sm">{f.farmer_name || "—"}</p>
                                        <p className="text-[11px] text-gray-400 mt-0.5">{f.farmer_mobile || ""}</p>
                                    </div>
                                    <span className={`font-bold text-sm flex-shrink-0 ml-2 ${
                                        balance > 0 ? "text-amber-600" : "text-gray-700"
                                    }`}>
                                        {fmtMoney(balance)}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Ledger entries */}
            <div className="bg-gray-50/60 rounded-xl p-4 border border-gray-100">
                <div className="mb-3 flex items-start justify-between gap-2">
                    <div>
                        <h3 className="font-bold text-gray-800 text-sm">Ledger entries</h3>
                        <p className="text-[11px] text-gray-400 mt-0.5">Approval posts the credit; each payout posts its debit explicitly.</p>
                    </div>
                    {isAdminRole && selectedFarmer && (
                        <button
                            type="button"
                            onClick={() => setShowAdjust(true)}
                            className="px-3 py-1.5 border border-gray-300 text-gray-700 text-[11px] font-bold rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
                        >
                            Manual Adjustment
                        </button>
                    )}
                </div>
                {!selectedFarmer ? (
                    <Empty label="Select a farmer to view entries" />
                ) : entriesLoading ? (
                    <Spinner label="Loading entries…" />
                ) : entries.length === 0 ? (
                    <Empty label="No ledger entries yet" />
                ) : (
                    <div className="flex flex-col gap-2">
                        {entries.map((e, i) => {
                            const isCredit = (e.entry_type || "").toLowerCase() === "credit";
                            const amount   = parseFloat(e.amount || 0);
                            const desc     = e.description || e.procurement_number || "";

                            return (
                                <div key={e.id || i} className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm flex items-start justify-between gap-2">
                                    <div className="flex items-start gap-2.5 min-w-0">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase flex-shrink-0 mt-0.5 ${
                                            isCredit
                                                ? DEBIT_CREDIT_STYLES.credit
                                                : DEBIT_CREDIT_STYLES.debit
                                        }`}>
                                            {isCredit ? "Credit" : "Debit"}
                                        </span>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-gray-800 text-xs capitalize">{e.source || ""}</p>
                                            {desc && <p className="text-[11px] text-gray-400 mt-0.5 truncate">{desc}</p>}
                                            <p className="text-[10px] text-gray-300 mt-0.5">{fmtDate(e.created_at)}</p>
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <span className={`font-bold text-sm block ${isCredit ? "text-green-600" : "text-red-500"}`}>
                                            {isCredit ? "+" : "−"}{fmtMoney(amount)}
                                        </span>
                                        <span className="text-[10px] text-gray-400">bal {fmtMoney(e.running_balance)}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <AnimatePresence>
                {showAdjust && selectedFarmer && (
                    <ManualAdjustmentModal
                        farmer={selectedFarmer}
                        onClose={() => setShowAdjust(false)}
                        onSaved={() => { loadFarmers(); loadStatement(); }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

// ─────────────────────────────────────────────
// FARMER PAYMENTS TAB
// ─────────────────────────────────────────────
const PAYMENT_STATUS_FILTERS = ["all", "pending", "completed", "failed"];

const FarmerPaymentsTab = () => {
    const [rows, setRows]       = useState([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage]       = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    const [statusFilter, setStatus] = useState("all");
    const [showAdd, setShowAdd] = useState(false);
    const [members, setMembers] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (statusFilter !== "all") params.status = statusFilter;
            const res  = await getFarmerPayments(params);
            const data = res?.data;
            setRows(data?.results || []);
            setTotal(data?.count || 0);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.response?.data?.message || "Unable to load payments.");
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        getFPOMembers({ page_size: 500 })
            .then((res) => setMembers(res?.data?.results || res?.data?.data || []))
            .catch(() => {});
    }, []);

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="font-bold text-gray-900 text-base">Farmer payments</h2>
                    <p className="text-gray-400 text-xs mt-0.5">Payouts against procurements — cash, UPI, bank transfer, cheque</p>
                </div>
                <button
                    type="button"
                    onClick={() => setShowAdd(true)}
                    className="flex items-center gap-2 bg-primary hover:bg-green-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-lg shadow-primary/20 flex-shrink-0"
                >
                    <FaPlus size={11} /> Record payment
                </button>
            </div>

            <div className="flex gap-1 mb-4">
                {PAYMENT_STATUS_FILTERS.map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => { setStatus(s); setPage(1); }}
                        className={`px-3 py-2 text-xs font-semibold rounded-lg capitalize transition-colors ${
                            statusFilter === s
                                ? "bg-primary text-white shadow"
                                : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                        {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                ))}
            </div>

            {loading ? <Spinner label="Loading payments…" /> : error ? (
                <ErrState msg={error} onRetry={load} />
            ) : rows.length === 0 ? (
                <Empty label="No payments recorded yet" />
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    {["FARMER", "PRC", "COMMODITY", "AMOUNT", "MODE", "REFERENCE", "DATE", "STATUS"].map((h) => (
                                        <th key={h} className="py-2 px-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap first:pl-0 last:pr-0">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="py-3 px-3 pl-0 whitespace-nowrap">
                                            <p className="font-medium text-gray-800 text-xs">{row.farmer?.name || "—"}</p>
                                            <p className="text-[10px] text-gray-400">{row.farmer?.mobile_number || ""}</p>
                                        </td>
                                        <td className="py-3 px-3 whitespace-nowrap text-xs text-gray-700">{row.procurement_number || "—"}</td>
                                        <td className="py-3 px-3 whitespace-nowrap text-xs text-gray-700">{row.commodity_name || "—"}</td>
                                        <td className="py-3 px-3 whitespace-nowrap font-bold text-gray-800 text-xs">{fmtMoney(row.amount)}</td>
                                        <td className="py-3 px-3 whitespace-nowrap text-xs text-gray-700 capitalize">{(row.payment_mode || "—").replace("_", " ")}</td>
                                        <td className="py-3 px-3 whitespace-nowrap text-xs text-gray-500">{row.reference_no || "—"}</td>
                                        <td className="py-3 px-3 whitespace-nowrap text-xs text-gray-500">{fmtDate(row.payment_date)}</td>
                                        <td className="py-3 px-3 pr-0 whitespace-nowrap"><StatusPill status={row.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPage={setPage} />
                </>
            )}

            <AnimatePresence>
                {showAdd && (
                    <PayFarmerModal
                        procurement={null}
                        members={members}
                        onClose={() => setShowAdd(false)}
                        onPaid={load}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

// ─────────────────────────────────────────────
// CAPACITY BAR
// ─────────────────────────────────────────────
const CapacityBar = ({ held, capacity }) => {
    const pct = capacity > 0 ? Math.min((held / capacity) * 100, 100) : 0;
    const color = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-primary";
    return (
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
            <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
        </div>
    );
};

// ─────────────────────────────────────────────
// WAREHOUSE CARD
// ─────────────────────────────────────────────
const WarehouseCard = ({ wh, isAdmin, onEdit, onDelete }) => (
    <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col gap-3"
    >
        <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 border border-gray-100">
                    <FaWarehouse className="text-gray-400 text-sm" />
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-gray-800 text-sm truncate">{wh.name}</p>
                        {wh.is_default && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-gray-300 text-gray-600 uppercase tracking-wider flex-shrink-0">
                                Default
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1 truncate">
                        <FaMapMarkerAlt size={9} />
                        {[wh.village, wh.address].filter(Boolean).join(" · ") || "no address"}
                    </p>
                </div>
            </div>
            {!wh.is_active && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 flex-shrink-0">
                    Inactive
                </span>
            )}
        </div>

        <div>
            <div className="flex items-center justify-between text-xs text-gray-500 mb-0.5">
                <span>{fmt(wh.total_stock_qty ?? wh.qty_held)} qtl held</span>
                <span>{fmt(wh.capacity_qtl)} qtl capacity</span>
            </div>
            <CapacityBar held={parseFloat(wh.total_stock_qty ?? wh.qty_held) || 0} capacity={parseFloat(wh.capacity_qtl) || 0} />
            <div className="flex items-center justify-between text-[11px] text-gray-400 mt-1">
                <span>Stock value</span>
                <span className="font-semibold text-gray-600">{fmtMoney(wh.total_stock_value)}</span>
            </div>
        </div>

        {isAdmin && (
            <div className="flex items-center gap-2 pt-1">
                <button
                    onClick={() => onEdit(wh)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    <FaEdit size={10} /> Edit
                </button>
                <button
                    onClick={() => onDelete(wh)}
                    className="flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors border border-gray-200"
                    title="Delete warehouse"
                >
                    <FaTrash size={10} />
                </button>
            </div>
        )}
    </motion.div>
);

// ─────────────────────────────────────────────
// WAREHOUSE FORM MODAL
// ─────────────────────────────────────────────
const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary bg-white placeholder:text-gray-400";
const textareaCls = "w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary bg-white placeholder:text-gray-400 resize-none";

const FieldWrap = ({ label, required, children }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const EMPTY_WH = { name: "", village: "", capacity_qtl: "", address: "", is_default: false, is_active: true };

const WarehouseFormModal = ({ editing, onClose, onSaved }) => {
    const [form, setForm]   = useState(editing ? {
        name:         editing.name || "",
        village:      editing.village || "",
        capacity_qtl: editing.capacity_qtl != null ? String(editing.capacity_qtl) : "",
        address:      editing.address || "",
        is_default:   editing.is_default ?? false,
        is_active:    editing.is_active ?? true,
    } : { ...EMPTY_WH });
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

    const handleSubmit = async () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "Name is required";
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSaving(true);
        try {
            const payload = {
                fpo:          getFPOData()?.id,
                name:         form.name.trim(),
                village:      form.village.trim() || null,
                capacity_qtl: form.capacity_qtl !== "" ? parseFloat(form.capacity_qtl) : null,
                address:      form.address.trim(),
                is_default:   form.is_default,
                is_active:    form.is_active,
            };
            if (editing) await updateWarehouse(editing.id, payload);
            else await addWarehouse(payload);
            toast.success(editing ? "Warehouse updated! ✅" : "Warehouse added! ✅");
            onSaved();
            onClose();
        } catch (e) {
            const msg = e?.response?.data?.message
                || Object.values(e?.response?.data?.errors || {}).flat().join(", ")
                || "Something went wrong.";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title={editing ? "Edit warehouse" : "Add warehouse"} subtitle="Name, village, address, capacity." onClose={onClose}>
            <div className="flex flex-col gap-4">
                <FieldWrap label="Name" required>
                    <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)}
                        placeholder="e.g. WH-3 Sagwa"
                        className={`${inputCls} ${errors.name ? "border-red-400" : ""}`} />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </FieldWrap>
                <div className="grid grid-cols-2 gap-3">
                    <FieldWrap label="Village">
                        <input type="text" value={form.village} onChange={(e) => set("village", e.target.value)} placeholder="e.g. Piploda" className={inputCls} />
                    </FieldWrap>
                    <FieldWrap label="Capacity (qtl)">
                        <input type="number" min="0" step="1" value={form.capacity_qtl} onChange={(e) => set("capacity_qtl", e.target.value)} placeholder="e.g. 2500" className={inputCls} />
                    </FieldWrap>
                </div>
                <FieldWrap label="Address">
                    <textarea rows={3} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street / landmark…" className={textareaCls} />
                </FieldWrap>
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2.5">
                        <Toggle id="wh-default" checked={form.is_default} onChange={() => set("is_default", !form.is_default)} />
                        <span className="text-sm font-medium text-gray-700">Default</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <Toggle id="wh-active" checked={form.is_active} onChange={() => set("is_active", !form.is_active)} />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                    </div>
                </div>
                <div className="flex gap-3 pt-2 border-t border-gray-200">
                    <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                    <button type="button" onClick={handleSubmit} disabled={saving} className="flex-1 py-2.5 bg-primary hover:bg-green-700 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary/20 transition-colors disabled:opacity-60">
                        {saving ? "Saving…" : "Save warehouse"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────
// DELETE WAREHOUSE MODAL
// ─────────────────────────────────────────────
const DeleteModal = ({ wh, onClose, onDeleted }) => {
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteWarehouse(wh.id);
            toast.success("Warehouse deleted.");
            onDeleted();
            onClose();
        } catch (e) {
            const msg = e?.response?.data?.message || "Something went wrong.";
            toast.error(msg);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Modal title="Delete warehouse" onClose={onClose}>
            <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
                    <FaExclamationTriangle className="text-red-400 text-2xl" />
                </div>
                <div>
                    <p className="font-bold text-gray-800">Delete <span className="text-primary">"{wh.name}"</span>?</p>
                    <p className="text-gray-500 text-xs mt-1">This action cannot be undone. All warehouse data will be permanently removed.</p>
                </div>
                <div className="flex gap-3 w-full">
                    <button onClick={onClose} className="flex-1 py-2.5 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                    <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-60">
                        {deleting ? "Deleting…" : "Yes, Delete"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─────────────────────────────────────────────
// WAREHOUSES TAB
// ─────────────────────────────────────────────
const WarehousesTab = ({ isAdmin, warehouses, onWarehousesChange }) => {
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [showAdd, setShowAdd]   = useState(false);
    const [editingWh, setEditingWh]   = useState(null);
    const [deletingWh, setDeletingWh] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res  = await getWarehouses();
            const data = res?.data;
            const list = data?.results || data?.data || [];
            onWarehousesChange(list);
        } catch {
            setError("Unable to connect. Please check the server.");
        } finally {
            setLoading(false);
        }
    }, [onWarehousesChange]);

    useEffect(() => { load(); }, [load]);

    return (
        <div>
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h2 className="font-bold text-gray-900 text-base">Warehouses</h2>
                    <p className="text-gray-400 text-xs mt-0.5">Settings-level CRUD · the default one is preselected on procurement entry</p>
                </div>
                {isAdmin && (
                    <button id="add-warehouse-btn" type="button" onClick={() => { setEditingWh(null); setShowAdd(true); }}
                        className="flex items-center gap-2 bg-primary hover:bg-green-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-lg shadow-primary/20 flex-shrink-0">
                        <FaPlus size={11} /> Add warehouse
                    </button>
                )}
            </div>

            {loading ? <Spinner label="Loading warehouses…" /> : error ? (
                <ErrState msg={error} onRetry={load} />
            ) : warehouses.length === 0 ? (
                <Empty icon={FaWarehouse} label="No warehouses added yet"
                    action={isAdmin && (
                        <button onClick={() => { setEditingWh(null); setShowAdd(true); }}
                            className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold flex items-center gap-2">
                            <FaPlus size={11} /> Add First Warehouse
                        </button>
                    )} />
            ) : (
                <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <AnimatePresence>
                        {warehouses.map((wh) => (
                            <WarehouseCard key={wh.id} wh={wh} isAdmin={isAdmin}
                                onEdit={(w) => { setEditingWh(w); setShowAdd(true); }}
                                onDelete={setDeletingWh} />
                        ))}
                    </AnimatePresence>
                </motion.div>
            )}

            <AnimatePresence>
                {showAdd && <WarehouseFormModal editing={editingWh} onClose={() => { setShowAdd(false); setEditingWh(null); }} onSaved={load} />}
            </AnimatePresence>
            <AnimatePresence>
                {deletingWh && <DeleteModal wh={deletingWh} onClose={() => setDeletingWh(null)} onDeleted={load} />}
            </AnimatePresence>
        </div>
    );
};

// ─────────────────────────────────────────────
// TABS CONFIG
// ─────────────────────────────────────────────
const TABS = [
    { key: "register",   label: "Procurement register" },
    { key: "stock",      label: "Stock & movements" },
    { key: "payments",   label: "Farmer payments" },
    { key: "ledger",     label: "Farmer ledger" },
    { key: "warehouses", label: "Warehouses" },
];

// ─────────────────────────────────────────────
// STAT CARD
// ─────────────────────────────────────────────
const StatCard = ({ label, value, sub }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex flex-col gap-1">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="text-[11px] text-gray-400">{sub}</p>}
    </div>
);

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────
const Procurement = () => {
    const [activeTab, setActiveTab]   = useState("register");
    const [warehouses, setWarehouses] = useState([]);
    const [summary, setSummary]       = useState(null);
    const { role, isAdmin, isAccountant, canApprove, canPayment, isDataEntry } = useRole();

    // Load summary for stat cards — GET /fpo/procurements/summary/ is unwrapped:
    // {today:{count,quantity,value}, this_month:{count,quantity,value}, pending_approval_count, unpaid_amount}
    const loadSummary = useCallback(async () => {
        try {
            const res  = await getProcurementSummary();
            setSummary(res?.data || null);
        } catch {
            // silently ignore — stats show — if summary fails
        }
    }, []);

    const loadWarehouses = useCallback(async () => {
        try {
            const res  = await getWarehouses();
            const data = res?.data;
            setWarehouses(data?.results || data?.data || []);
        } catch {
            // silently ignore — dropdown just stays empty if this fails
        }
    }, []);

    useEffect(() => { loadSummary(); }, [loadSummary]);
    useEffect(() => { loadWarehouses(); }, [loadWarehouses]);

    const stats = [
        {
            label: "Pending Approval",
            value: summary?.pending_approval_count ?? "—",
            sub:   "entries waiting on the gate",
        },
        {
            label: "Today",
            value: summary?.today?.count ?? "—",
            sub:   summary?.today ? `${fmt(summary.today.quantity)} qtl · ${fmtMoney(summary.today.value)}` : "no entries yet",
        },
        {
            label: "This Month",
            value: summary?.this_month?.count ?? "—",
            sub:   summary?.this_month ? `${fmt(summary.this_month.quantity)} qtl · ${fmtMoney(summary.this_month.value)}` : "no entries yet",
        },
        {
            label: "Unpaid to Farmers",
            value: summary?.unpaid_amount != null ? fmtMoney(summary.unpaid_amount) : "₹—",
            sub:   "approved minus paid",
        },
    ];

    // data_entry/field_staff: create/list procurements + view stock/warehouses only —
    // no access at all to farmer-payments or the ledger.
    const visibleTabs = isDataEntry
        ? TABS.filter((t) => ["register", "stock", "warehouses"].includes(t.key))
        : TABS;

    return (
        <div className="w-full pb-12">
            {/* Page header */}
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200 -mx-4 -mt-4 mb-5 px-4 sm:px-6 py-4">
                <h1 className="text-gray-900 text-lg font-bold">Procurement</h1>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                {stats.map((s) => (
                    <StatCard key={s.label} label={s.label} value={s.value} sub={s.sub} />
                ))}
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
                {visibleTabs.map((tab) => (
                    <button
                        key={tab.key}
                        id={`tab-${tab.key}`}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                            activeTab === tab.key
                                ? "border-primary text-primary"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <AnimatePresence mode="wait">
                    {activeTab === "register" && (
                        <motion.div key="register" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <ProcurementRegisterTab
                                warehouses={warehouses}
                                canApprove={canApprove}
                                canPayment={canPayment}
                                isAccountant={isAccountant}
                                isDataEntry={isDataEntry}
                                onDataChange={loadSummary}
                            />
                        </motion.div>
                    )}
                    {activeTab === "stock" && (
                        <motion.div key="stock" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <StockMovementsTab />
                        </motion.div>
                    )}
                    {activeTab === "payments" && (
                        <motion.div key="payments" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <FarmerPaymentsTab />
                        </motion.div>
                    )}
                    {activeTab === "ledger" && (
                        <motion.div key="ledger" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <FarmerLedgerTab isAdminRole={role === "fpo_admin"} />
                        </motion.div>
                    )}
                    {activeTab === "warehouses" && (
                        <motion.div key="warehouses" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <WarehousesTab
                                isAdmin={isAdmin}
                                warehouses={warehouses}
                                onWarehousesChange={setWarehouses}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Procurement;
